import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

interface UserInfo {
  id: number;
  email: string;
  approved: number;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
  roles?: string[];
  profilePictureUrl?: string | null;
  organizationName?: string | null;
}

interface UserState {
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
};

// Async thunk for fetching user information
export const fetchUserInfo = createAsyncThunk<UserInfo, { token: string }, { rejectValue: string }>(
  'user/fetchUserInfo',
  async ({ token }, { rejectWithValue }) => {
    try {
      const isWorkOS = document.cookie.split('; ').some((r) => r.startsWith('workos_session='));
      const endpoint = isWorkOS
        ? `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/auth/user/client-info`
        : `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/auth/user/client-info`;

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const client = response.data?.client;
      const data = Array.isArray(client) ? client[0] : client || response.data;
      // Organization name comes from WorkOS (same endpoint Inside uses); the auth client-info has none
      if (!data.organizationName) {
        try {
          const orgRes = await axios.get(`${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/workos/user/client-info`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const orgClient = orgRes.data?.client;
          const orgData = Array.isArray(orgClient) ? orgClient[0] : orgClient;
          if (orgData?.organizationName) data.organizationName = orgData.organizationName;
        } catch { /* keep the cookie fallback in MainNavigation */ }
      }
      // Read profile picture from JWT `picture` claim (set at login time from OAuth provider)
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload?.picture) data.profilePictureUrl = payload.picture;
      } catch { /* ignore */ }
      return data;
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message);
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      localStorage.removeItem('user');
    },
    setUserFromCache: (state, action: PayloadAction<UserInfo>) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action: PayloadAction<UserInfo>) => {
        state.loading = false;
        state.user = action.payload;
        localStorage.setItem('user', JSON.stringify(action.payload));
      })
      .addCase(fetchUserInfo.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || null;
      });
  },
});

export const { resetUserState, setUserFromCache } = userSlice.actions;
export default userSlice.reducer;
